import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    DropdownWidget,
    InputOutputType,
    IntegerSpecificationModel,
    ModulePropertySpecification,
    ModuleWidget,
    NumberSpecificationModel,
    RadioButtonGroupWidget,
    RangeSliderWidget,
    RangeSpecificationModel,
    SliderWidget,
    SpecificationPropertyObject,
    StringResource,
} from '@ca/core/models';
import { distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
    selector: 'ca-ui-widget-demo-controls',
    templateUrl: './ui-widget-demo-controls.component.html',
})
export class UiWidgetDemoControlsComponent implements AfterViewInit {
    @Input() activeProperty: SpecificationPropertyObject;
    @Output() onFormValidityChanged = new EventEmitter<boolean>();

    @ViewChild('form') form: NgForm;

    readonly textMaxWidth: number = 200;
    readonly integerPattern: string = '^([1-9][0-9]*)|([0])$';

    get widgetUiProperty(): ModuleWidget | undefined {
        return this.activeProperty.widgetSpecificUiProperty;
    }

    get widgetSpecProperty(): ModulePropertySpecification {
        return this.activeProperty.widgetSpecificSpecProperty;
    }

    get sliderStep(): number {
        let step: number = 1;

        if ((this.widgetUiProperty as SliderWidget | RangeSliderWidget).step != null) {
            step = (this.widgetUiProperty as SliderWidget | RangeSliderWidget).step!;
        } else {
            if (this.activeProperty.uiControlType === 'slider' || this.activeProperty.uiControlType === 'slider_range') {
                // #320940: If step is not defined in widgetUiProperty, set it to a default step value.
                // This will set it in the module-specification.
                (this.widgetUiProperty as SliderWidget | RangeSliderWidget).step = step;
            }
        }

        return step;
    }

    set sliderStep(val: number) {
        (this.widgetUiProperty as SliderWidget | RangeSliderWidget).step = val;
    }

    get sliderMaxValue(): number {
        // Predefine as 10
        let maxValue: number | undefined = 10;

        switch (this.activeProperty.specType) {
            case 'integer':
                maxValue = (this.widgetSpecProperty as IntegerSpecificationModel).max;
                break;
            case 'number':
                maxValue = (this.widgetSpecProperty as NumberSpecificationModel).upper_inclusive;
                break;
            case 'range':
                maxValue = (this.widgetSpecProperty as RangeSpecificationModel).upper!.upper_inclusive;
                break;
        }

        // TODO: Safety check: Why can the checks above fail and return null????
        if (maxValue == null) {
            maxValue = 10;

            //  Set to 10 in JSON as well
            this.sliderMaxValue = maxValue;
        }

        return maxValue;
    }

    get sliderMinValue(): number {
        let minValue: number | undefined = 0;

        switch (this.activeProperty.specType) {
            case 'integer':
                minValue = (this.widgetSpecProperty as IntegerSpecificationModel).min;
                break;
            case 'number':
                minValue = (this.widgetSpecProperty as NumberSpecificationModel).lower_inclusive;
                break;
            case 'range':
                minValue = (this.widgetSpecProperty as RangeSpecificationModel).lower!.lower_inclusive;
                break;
        }

        // TODO: Safety check: Why can the checks above fail and return null????
        if (minValue == null) {
            minValue = 0;

            // Set to 0 in JSON as well
            this.sliderMinValue = minValue;
        }

        return minValue;
    }

    set sliderMinValue(val: number) {
        if (!isNaN(val)) {
            switch (this.activeProperty.specType) {
                case 'integer':
                    (this.widgetSpecProperty as IntegerSpecificationModel).min = val;
                    break;
                case 'number':
                    (this.widgetSpecProperty as NumberSpecificationModel).lower_inclusive = val;
                    break;
                case 'range':
                    if ((this.widgetSpecProperty as RangeSpecificationModel).lower == null) {
                        (this.widgetSpecProperty as RangeSpecificationModel).lower = {};
                    }
                    (this.widgetSpecProperty as RangeSpecificationModel).lower!.lower_inclusive = val;
                    break;
            }
        }
    }

    set sliderMaxValue(val: number) {
        if (!isNaN(val)) {
            switch (this.activeProperty.specType) {
                case 'integer':
                    (this.widgetSpecProperty as IntegerSpecificationModel).max = val;
                    break;
                case 'number':
                    (this.widgetSpecProperty as NumberSpecificationModel).upper_inclusive = val;
                    break;
                case 'range':
                    if ((this.widgetSpecProperty as RangeSpecificationModel).upper == null) {
                        (this.widgetSpecProperty as RangeSpecificationModel).upper = {};
                    }
                    (this.widgetSpecProperty as RangeSpecificationModel).upper!.upper_inclusive = val;
                    break;
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get defaultValue(): any {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
        let defaultValue: any | undefined = this.activeProperty.specProperty.default;

        // For slider the default value can not be null
        if (this.activeProperty.uiControlType === 'slider' && defaultValue == null) {
            defaultValue = this.sliderMinValue;
        } else if (this.activeProperty.uiControlType === 'slider_range') {
            if (this.activeProperty.specProperty.default != null && this.activeProperty.specProperty.default.lower_inclusive != null) {
                defaultValue = this.activeProperty.specProperty.default.lower_inclusive;
            } else {
                defaultValue = this.sliderMinValue;

                // If there's no lower default value, set it to min value
                this.defaultValue = defaultValue;
            }
        }
        return defaultValue;
    }

    set defaultValue(val: number) {
        if (!isNaN(val)) {
            if (this.activeProperty.specType === 'range') {
                if (this.activeProperty.specProperty.default != null && typeof this.activeProperty.specProperty.default === 'object') {
                    this.activeProperty.specProperty.default.lower_inclusive = val;
                } else {
                    this.activeProperty.specProperty.default = {
                        lower_inclusive: val,
                    };
                }
            } else {
                this.activeProperty.specProperty.default = val;
            }
        }
    }

    set highSliderDefaultValue(val: number) {
        // If the value is NaN, set it to the maxSliderValue
        if (isNaN(val)) {
            val = this.sliderMaxValue;
        }

        if (this.activeProperty.specProperty.default != null && this.activeProperty.specProperty.default.upper_inclusive != null) {
            if (this.activeProperty.specProperty.default.upper_inclusive) {
                this.activeProperty.specProperty.default.upper_inclusive = val;
            } else {
                this.activeProperty.specProperty.default.upper_inclusive = val;
            }
        } else {
            this.activeProperty.specProperty.default = new NumberSpecificationModel();
            this.activeProperty.specProperty.default.upper_inclusive = val;
        }
    }

    get highSliderDefaultValue(): number {
        let highSliderDefaultValue: number | undefined = undefined;
        if (this.activeProperty.specProperty.default != null && this.activeProperty.specProperty.default.upper_inclusive != null) {
            highSliderDefaultValue = this.activeProperty.specProperty.default.upper_inclusive;
        }

        // Falback: If no value is set, set to max value.
        if (highSliderDefaultValue == null) {
            highSliderDefaultValue = this.sliderMaxValue;

            // Set that to the JSON as well.
            this.highSliderDefaultValue = highSliderDefaultValue;
        }

        return highSliderDefaultValue;
    }

    get isActivePropertyInput(): boolean {
        return this.activeProperty != null && this.activeProperty.type === InputOutputType.Input ? true : false;
    }

    ngAfterViewInit(): void {
        // Initially emit event
        if (this.form) {
            this.onFormValidityChanged.emit(true);
        }

        this.form.statusChanges
            ?.pipe(distinctUntilChanged())
            .pipe(
                tap((status: 'VALID' | 'INVALID') => {
                    const isFormValid = status === 'VALID';
                    this.onFormValidityChanged.emit(isFormValid);
                }),
            )
            .subscribe();
    }

    getDropdownOrRadioItems(): StringResource[] {
        let items: StringResource[] = new Array<StringResource>();
        if (this.activeProperty && (this.activeProperty.widgetSpecificUiProperty as RadioButtonGroupWidget | DropdownWidget).items) {
            items = (this.activeProperty.widgetSpecificUiProperty as RadioButtonGroupWidget | DropdownWidget).items;
        }

        // This is done to trigger changeDetection in the component.
        // Otherwise it does not detect any changes of the array
        const newArr = [...items];
        return newArr;
    }

    setDefaultValueFromInput(event: FocusEvent) {
        this.defaultValue = this.parseNumberValueOfEvent(event);
    }

    setHighSliderDefaultValueFromInput(event: FocusEvent) {
        this.highSliderDefaultValue = this.parseNumberValueOfEvent(event);
    }

    setMinValueFromInput(event: FocusEvent) {
        this.sliderMinValue = this.parseNumberValueOfEvent(event);
    }

    setMaxValueFromInput(event: FocusEvent) {
        this.sliderMaxValue = this.parseNumberValueOfEvent(event);
    }

    setSliderStepFromInput(event: FocusEvent): void {
        this.sliderStep = this.parseNumberValueOfEvent(event);
    }

    setSliderValue(val: number): void {
        this.defaultValue = val;
    }

    setRangeSliderValue(val: number[]): void {
        this.defaultValue = val[0];
        this.highSliderDefaultValue = val[1];
    }

    private parseNumberValueOfEvent(event: FocusEvent): number {
        let eventVal: string | number | null | undefined = event.target && (event.target as EventTarget & { [key: string]: string | number | undefined }).value;
        if (eventVal != null) {
            eventVal = parseFloat(eventVal as string);
        } else {
            // If the number is not defined, set a default value
            eventVal = 0;
        }

        return eventVal;
    }
}
